import type { ApolloError, ApolloQueryResult } from '@apollo/client';
import { createContext, useRef, useState } from 'react';

import { ContentArticleStatus } from '@xing-com/crate-common-graphql-types';
import { useUpdateEffect } from '@xing-com/crate-entity-pages-common';
import { useEditStore } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';

import {
  INSIDER_ARTICLE_EDITOR_PATH,
  INSIDER_SHARE_PATH,
  OLD_NEWS_NEW_ARTICLE,
  OLD_NEWS_NEW_ARTICLE_LINK,
} from '../../constants/urls';
import type {
  EntityPagesArticleModuleListQuery,
  EntityPagesArticleModuleListQueryVariables,
} from '../../graphql/queries/entity-pages-article-module-list.gql-types';
import { useArticleCards } from '../../hooks/use-article-cards/use-article-cards';
import type { ArticleCardView } from '../../types/types';

type ArticlesModuleContextType = {
  variablesQuery: EntityPagesArticleModuleListQueryVariables;
  articlesCards: ArticleCardView[];
  error?: ApolloError;
  initialLoading: boolean;
  articlesUrns: string[];
  metadata: {
    allAmount?: number;
    publishedArticlesCount?: number;
    scheduledArticlesCount?: number;
    draftArticlesCount?: number;
    disabledArticlesCount?: number;
  };
  isFeatureArticleVisible: boolean;
  isEmptyState: boolean;
  isShowMoreAvailable: boolean;
  articleSharePath: string;
  articleEditorPath: string;
  updateFilter: (navigationStatus?: ContentArticleStatus) => void;
  fetchMoreLoading: boolean;
  showMoreArticles: () => Promise<void>;
  refetchLoading: boolean;
  refetch: () => Promise<ApolloQueryResult<EntityPagesArticleModuleListQuery>>;
};

export const ArticlesModuleContext = createContext<
  ArticlesModuleContextType | undefined
>(undefined);

const LIMIT = 3;

type Props = {
  children: (value: ArticlesModuleContextType) => React.ReactNode;
};
export const ArticlesModuleContextProvider: React.FC<Props> = ({
  children,
}) => {
  const { pageContext } = usePageContext();
  const isEditing = useEditStore(({ isEditing }) => isEditing);

  const showMoreSize = useRef(LIMIT);

  const pageCppId = pageContext.cppId ?? '';
  const pageCppIdSlug = pageCppId.split('.')[0];
  const globalId = `surn:x-xing:content:publisher_page:${pageCppId}`;

  const [navigationStatus, setNavigationStatus] = useState<
    ContentArticleStatus | undefined
  >(isEditing ? undefined : ContentArticleStatus.Published);

  useUpdateEffect(() => {
    setNavigationStatus(isEditing ? undefined : ContentArticleStatus.Published);
  }, [isEditing]);

  const isValidNavigationForFeatured =
    navigationStatus === undefined ||
    navigationStatus === ContentArticleStatus.Published;

  const variablesQuery = {
    pageUrn: globalId,
    status: navigationStatus,
    first: LIMIT,
    requestMetadata: isEditing,
    requestFeaturedArticle: isValidNavigationForFeatured,
  };

  const {
    articlesCards,
    metadata,
    hasFeaturedArticle,
    hasNextPage,
    fetchMoreLoading,
    initialLoading,
    refetchLoading,
    error,
    refetch,
    fetchMore,
  } = useArticleCards(variablesQuery);

  const articleSharePath = pageContext.insiderPageName
    ? INSIDER_SHARE_PATH(pageContext.insiderPageName)
    : OLD_NEWS_NEW_ARTICLE_LINK(pageCppIdSlug);
  const articleEditorPath = pageContext.insiderPageName
    ? INSIDER_ARTICLE_EDITOR_PATH(pageContext.insiderPageName)
    : OLD_NEWS_NEW_ARTICLE(pageCppIdSlug);

  const articlesUrns = articlesCards.reduce<string[]>(
    (urns, article) => (article.urn ? [...urns, article.urn] : urns),
    []
  );

  const isFeatureArticleVisible =
    isEditing && !hasFeaturedArticle && isValidNavigationForFeatured;
  const isEmptyState = articlesCards.length === 0;
  const isShowMoreAvailable = hasNextPage;

  const handleOnFilter = (navigationStatus?: ContentArticleStatus) => {
    setNavigationStatus(navigationStatus);
    showMoreSize.current = LIMIT;
  };
  const handleShowMoreArticles = async () => {
    await fetchMore();
    showMoreSize.current += LIMIT;
  };

  const values = {
    variablesQuery: variablesQuery,
    articlesCards,
    error,
    initialLoading,
    articlesUrns,
    metadata,
    isFeatureArticleVisible,
    isEmptyState,
    isShowMoreAvailable,
    articleSharePath,
    articleEditorPath,
    updateFilter: handleOnFilter,
    fetchMoreLoading,
    showMoreArticles: handleShowMoreArticles,
    refetchLoading,
    refetch: () => refetch(showMoreSize.current),
  };

  return (
    <ArticlesModuleContext.Provider value={values}>
      {children(values)}
    </ArticlesModuleContext.Provider>
  );
};
